// src/components/Lms/Pages/CarreraCurriculumView.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiChevronDown, FiChevronUp, FiBook, FiLayers, FiFileText, FiMessageSquare } from 'react-icons/fi';
import { API_BASE } from '../../../api';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import { Models as LmsModels } from '../Utils/ModelsLMS';
import { getSessionUserId } from '../../../utils/SessionUser';
import AlertDialog from '../Components/Shared/AlertDialog';
import ObservacionesCarreraModal from '../Modals/ObservacionesCarreraModal';
import '../Styles/LmsExtra.css';

const CarreraCurriculumView = () => {
    const { id: idCarrera } = useParams();
    const navigate = useNavigate();
    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');

    // Estados
    const [carrera, setCarrera] = useState(null);
    const [semestres, setSemestres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI
    const [expandedSemesters, setExpandedSemesters] = useState({});
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "Información", message: "", variant: "info" });
    const [approving, setApproving] = useState(false);
    const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

    // Cargar datos iniciales
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Obtener Carrera
            const resCarrera = await axios.get(LMS_ENDPOINTS.carrerasById(idCarrera));
            const carreraData = Array.isArray(resCarrera.data) ? resCarrera.data[0] : resCarrera.data;
            if (carreraData) {
                setCarrera(LmsModels.carrera(carreraData));
            }

            // 2. Obtener Catálogos
            const [resNiveles, resHabilidades] = await Promise.all([
                axios.get(LMS_ENDPOINTS.catNivel),
                axios.get(LMS_ENDPOINTS.catTipoHabilidad)
            ]);
            const niveles = (resNiveles.data || []).map(LmsModels.niveles);
            const habilidades = (resHabilidades.data || []).map(LmsModels.tipoHabilidad);

            // 3. Obtener Semestres (Filtrados por Carrera)
            const resSemestres = await axios.get(LMS_ENDPOINTS.semestres);
            const semestresFiltrados = (resSemestres.data || [])
                .map(LmsModels.semestre)
                .filter(s => Number(s.idCarrera) === Number(idCarrera) && s.activo)
                .sort((a, b) => (a.idSemestre) - (b.idSemestre));

            const resSemestreCursos = await axios.get(LMS_ENDPOINTS.semestreCurso);
            const semCursosData = (resSemestreCursos.data || []).map(LmsModels.semestreCurso);

            const resTodosCursos = await axios.get(LMS_ENDPOINTS.cursosAdminHistorico);
            const todosCursos = (resTodosCursos.data || []).map(LmsModels.cursos);

            const semestresMapeados = semestresFiltrados.map(s => {
                const idS = s.idSemestre;
                return {
                    ...s,
                    id: idS,
                    cursos: semCursosData
                        .filter(sc => Number(sc.idSemestre) === Number(idS) && (sc.activo))
                        .map(sc => {
                            const cursoInfo = todosCursos.find(c => Number(c.idCurso) === Number(sc.idCurso));
                            if (!cursoInfo) return null;

                            const nivel = niveles.find(n => n.idNivel === cursoInfo.idNivel);
                            const habilidad = habilidades.find(h => h.idTipoHabilidad === cursoInfo.idTipoHabilidad);

                            return {
                                idSemestreCurso: sc.idSemestreCurso,
                                idCurso: sc.idCurso,
                                nombre: cursoInfo.nombre,
                                imagen: cursoInfo.uRLRutaImagen,
                                nivel: nivel ? nivel.nombre : '-',
                                habilidad: habilidad ? habilidad.nombre : '-',
                                duracion: cursoInfo.duracion
                            };
                        }).filter(Boolean)
                };
            });

            setSemestres(semestresMapeados);

            // Expandir todos por defecto
            const next = {};
            semestresMapeados.forEach(s => { next[s.id] = true; });
            setExpandedSemesters(next);

        } catch (err) {
            console.error('Error al cargar currículo de carrera:', err);
            setError('No se pudo cargar la información de la carrera.');
        } finally {
            setLoading(false);
        }
    }, [idCarrera]);

    useEffect(() => {
        loadData();
    }, [idCarrera, loadData]);

    const toggleSemester = (id) => {
        setExpandedSemesters(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleAprobarRevision = async () => {
        setApproving(true);
        try {
            const idUsuarioAdmin = getSessionUserId();

            // 1. Obtener la petición pendiente
            const resPeticiones = await axios.get(LMS_ENDPOINTS.peticionRevisionCarreraByCarrera(idCarrera));
            const peticiones = resPeticiones.data || [];
            const peticionPendiente = peticiones.find(p => p.atendido === false || p.Atendido === false);

            if (!peticionPendiente) {
                console.warn("No se encontró petición pendiente. Se procederá con la generación de certificado.");
            }

            // 2. Obtener firma activa del director (Tal como en CarreraCurriculum.jsx)
            const resFirmas = await axios.get(LMS_ENDPOINTS.firmas);
            const firmas = resFirmas.data || [];
            const directorFirma = firmas.find(f => (f.esDirector || f.EsDirector) && (f.activo || f.Activo));

            if (!directorFirma) {
                throw new Error("No se encontró una firma activa de Director para generar el certificado oficial.");
            }

            const idFirmaFinal = directorFirma.idFirma || directorFirma.IdFirma;

            // 3. Generar Certificado de la Carrera
            const generatePayload = {
                IdCarrera: Number(idCarrera),
                IdFirma: Number(idFirmaFinal)
            };

            // Usamos timeout largo igual que en el instructor
            const generateRes = await axios.post(`${LMS_ENDPOINTS.certificadoCarrera}/Generate`, generatePayload, {
                timeout: 30000
            });
            const newIdCertificado = generateRes.data;

            if (!newIdCertificado) throw new Error("El servidor no devolvió un ID de certificado válido.");

            // 4. Actualizar la carrera: idCertificado = newIdCertificado, pero SIN publicar aún
            const carreraDto = {
                IdCarrera: carrera.idCarrera || carrera.IdCarrera,
                Nombre: carrera.nombre,
                Descripcion: carrera.descripcion,
                IdInstructor: carrera.idInstructor,
                IdCertificado: newIdCertificado,
                Activado: carrera.activo !== undefined ? carrera.activo : true,
                UrlImagen: carrera.urlImagen || null,
                Publicado: false // El instructor decidirá cuándo publicar
            };

            await axios.put(LMS_ENDPOINTS.carreras, carreraDto);

            // 5. Marcar petición como atendida si existe
            if (peticionPendiente) {
                const peticionActualizada = {
                    IdPeticionRevisionCarrera: peticionPendiente.idPeticionRevisionCarrera || peticionPendiente.IdPeticionRevisionCarrera,
                    IdCarrera: peticionPendiente.idCarrera || peticionPendiente.IdCarrera,
                    IdUsuarioSolicitante: peticionPendiente.idUsuarioSolicitante || peticionPendiente.IdUsuarioSolicitante,
                    IdUsuarioSolicitado: peticionPendiente.idUsuarioSolicitado || peticionPendiente.IdUsuarioSolicitado,
                    FechaPeticion: peticionPendiente.fechaPeticion || peticionPendiente.FechaPeticion,
                    Atendido: true,
                    IdUsuarioAtendio: parseInt(idUsuarioAdmin, 10),
                    Activo: peticionPendiente.activo !== undefined ? peticionPendiente.activo : true
                };

                await axios.put(LMS_ENDPOINTS.peticionRevisionCarrera, peticionActualizada);
            }

            setCarrera(prev => ({ ...prev, idCertificado: newIdCertificado }));

            setAlertDialog({
                open: true,
                title: "Revisión Aprobada",
                message: "Se ha generado el certificado de la carrera. El instructor ya puede realizar la publicación final.",
                variant: "success",
                redirectOnClose: true
            });

        } catch (error) {
            console.error("Error aprobando revisión:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "No se pudo aprobar la revisión. Verifica los logs del servidor.",
                variant: "error"
            });
        } finally {
            setApproving(false);
        }
    };

    if (loading) return <div className="cc-page">Cargando trayectoria académica...</div>;
    if (error || !carrera) return <div className="cc-page"><p style={{ color: '#dc2626' }}>{error || 'Carrera no encontrada.'}</p></div>;

    const allExpanded = Object.values(expandedSemesters).every(v => v);

    return (
        <div className="cc-page">
            <div className="cc-breadcrumb">
                <button type="button" className="cc-breadcrumb-back" onClick={() => navigate("/menu/lms/catalogo-carreras")}>
                    <FiArrowLeft /> Volver
                </button>
            </div>

            <div className="cc-layout">
                <div className="cc-main">
                    <h1 className="cc-course-title">
                        {carrera.nombre}
                        {carrera.publicado && (
                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '1.2rem', marginLeft: '8px' }}>
                                ✓ Publicado
                            </span>
                        )}
                    </h1>

                    <div className="cc-chip-row">
                        <span className="cc-chip">{semestres.length} niveles</span>
                        <span className="cc-chip">{semestres.reduce((acc, s) => acc + s.cursos.length, 0)} cursos totales</span>
                    </div>

                    <div className="cc-toolbar">
                        <button type="button" className="cc-add-lesson-btn" onClick={() => {
                            const next = {};
                            if (!allExpanded) semestres.forEach(s => { next[s.id] = true; });
                            setExpandedSemesters(next);
                        }}>
                            {allExpanded ? "Contraer todas" : "Expandir todas"}
                        </button>
                    </div>

                    <div className="cc-modules-list">
                        {semestres.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '3rem', background: '#f9fafb', borderRadius: '16px', border: '2px dashed #e5e7eb' }}>
                                <FiLayers style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem' }} />
                                <p style={{ color: '#6b7280' }}>Aún no hay niveles definidos.</p>
                            </div>
                        ) : (
                            semestres.map((semestre, idx) => (
                                <div key={semestre.id} className="cc-module-card">
                                    <div className="cc-module-header" onClick={() => toggleSemester(semestre.id)} style={{ cursor: 'pointer' }}>
                                        <div className="cc-module-header-left">
                                            <div className="cc-module-index-badge">{idx + 1}</div>
                                            <div>
                                                <h2 className="cc-module-title">{semestre.nombre}</h2>
                                                <p className="cc-module-meta">{semestre.cursos.length} cursos</p>
                                            </div>
                                        </div>
                                        <div className="cc-module-header-right">
                                            <button type="button" className="cc-module-toggle-btn">
                                                {expandedSemesters[semestre.id] ? <FiChevronUp /> : <FiChevronDown />}
                                            </button>
                                        </div>
                                    </div>

                                    {expandedSemesters[semestre.id] && (
                                        <div className="cc-module-content">
                                            {semestre.cursos.map((curso) => (
                                                <div key={curso.idSemestreCurso} className="cc-lesson-row">
                                                    <div className="cc-lesson-main">
                                                        <div className="cc-lesson-icon" style={{ padding: 0, overflow: 'hidden', width: 42, height: 42, borderRadius: 8 }}>
                                                            {curso.imagen ? (
                                                                <img src={curso.imagen.startsWith('http') ? curso.imagen : `${SERVER_URL}${curso.imagen}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                <FiBook />
                                                            )}
                                                        </div>
                                                        <div className="cc-lesson-texts">
                                                            <h3 className="cc-lesson-title">{curso.nombre}</h3>
                                                            <p className="cc-lesson-meta">
                                                                <span style={{ fontWeight: 600 }}>{curso.nivel}</span>
                                                                <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                                                                <span>{curso.habilidad}</span>
                                                                <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                                                                <span>{curso.duracion} días</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="cc-lesson-actions">
                                                        <button
                                                            type="button"
                                                            className="cc-lesson-btn cc-lesson-btn-ghost"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/menu/lms/curso-curriculo-revision-view/${curso.idCurso}`, { state: { fromCarreraId: idCarrera } });
                                                            }}
                                                            title="Ver currículum del curso"
                                                        >
                                                            <FiFileText /> Ver
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <aside className="cc-sidebar">
                    <div className="cc-summary-card">
                        <h3 className="cc-summary-title">Resumen</h3>
                        <div className="cc-summary-grid">
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">Niveles</small>
                                <strong className="cc-summary-value">{semestres.length}</strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">Cursos</small>
                                <strong className="cc-summary-value">{semestres.reduce((acc, s) => acc + s.cursos.length, 0)}</strong>
                            </div>
                        </div>
                        <div style={{ marginTop: '1.5rem' }}>
                            <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                                {carrera.descripcion || 'Sin descripción disponible.'}
                            </p>
                        </div>

                        {/* Separador visual */}
                        <hr style={{ margin: '1.5rem 0', border: '0', borderTop: '1px solid #e2e8f0' }} />

                        {carrera && !carrera.publicado && (
                            carrera.idCertificado ? (
                                <div style={{
                                    padding: '12px',
                                    background: '#e0f2fe',
                                    color: '#0369a1',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '0.95rem',
                                    border: '1px solid #bae6fd',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}>
                                    <span>📜 Certificado Generado (Aprobado)</span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="cc-summary-btn-main"
                                    onClick={handleAprobarRevision}
                                    disabled={approving}

                                    style={{
                                        marginBottom: '1rem',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        opacity: approving ? 0.7 : 1,
                                        cursor: approving ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {approving ? 'Procesando...' : '📜 Aprobar y Generar Certificado'}
                                </button>
                            )
                        )}

                        <p className="text-xs text-gray-400 text-center" style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8' }}>
                            Estás navegando en modo de revisión.
                        </p>
                    </div>
                </aside>
            </div>

            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => {
                    setAlertDialog({ ...alertDialog, open: false });
                    if (alertDialog.redirectOnClose) {
                        navigate("/menu/lms/catalogo-carreras");
                    }
                }}
            />

            {/* Modal de Observaciones */}
            <ObservacionesCarreraModal
                isOpen={isObservacionesModalOpen}
                onClose={() => setIsObservacionesModalOpen(false)}
                idCarrera={idCarrera}
            />

            {/* Floating Button for Observations */}
            <button
                className="floating-observaciones-btn"
                onClick={() => setIsObservacionesModalOpen(true)}
                title="Historial de Observaciones"
                style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    fontSize: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    zIndex: 1000
                }}
            >
                <FiMessageSquare />
            </button>
        </div>
    );
};

export default CarreraCurriculumView;
